import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../../shared/api/client";
import { standalone, applicationServerKey, isInstallPromptEvent, type InstallPromptEvent } from "./technical-mobile-browser";
import { parseMobileIdentity, parsePushConfiguration, type Identity, type PushConfiguration } from "./technical-mobile-contract";

export function useTechnicalMobileSetup() {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [configuration, setConfiguration] = useState<PushConfiguration | null>(null);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(standalone);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const mounted = useRef(false);
  const inFlight = useRef(false);
  const epoch = useRef(0);
  const isIos = /iphone|ipad|ipod/iu.test(navigator.userAgent);

  useEffect(() => {
    let active = true;
    mounted.current = true;
    epoch.current += 1;
    inFlight.current = false;
    setBusy(false);
    const beforeInstall = (event: Event) => {
      if (!active || !isInstallPromptEvent(event)) return;
      event.preventDefault();
      setInstallPrompt(event);
    };
    const installedHandler = () => { if (active) setInstalled(true); };
    window.addEventListener("beforeinstallprompt", beforeInstall);
    window.addEventListener("appinstalled", installedHandler);
    void (async () => {
      try {
        const currentIdentity = parseMobileIdentity(await apiRequest<unknown>("/api/production/technical-mobile/session", { cache: "no-store" }));
        if (!active) return;
        setIdentity(currentIdentity);
        const pushConfiguration = parsePushConfiguration(await apiRequest<unknown>("/api/production/technical-mobile/push-config", { cache: "no-store" }));
        if (!active) return;
        setConfiguration(pushConfiguration);
        if (pushConfiguration.configured && "serviceWorker" in navigator && "PushManager" in window) {
          const registration = await navigator.serviceWorker.ready;
          if (!active) return;
          const existing = await registration.pushManager.getSubscription();
          if (existing && active) {
            await apiRequest("/api/production/technical-mobile/push-subscriptions", {
              method: "POST",
              body: JSON.stringify({ ...existing.toJSON(), deviceLabel: navigator.userAgent }),
            });
            if (active) setSubscribed(true);
          }
        }
      } catch {
        if (active) setIdentity(null);
      }
    })();
    return () => {
      active = false;
      mounted.current = false;
      epoch.current += 1;
      window.removeEventListener("beforeinstallprompt", beforeInstall);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  async function install() {
    const generation = epoch.current;
    const isCurrent = () => mounted.current && epoch.current === generation;
    if (!isCurrent() || inFlight.current || !installPrompt) return;
    inFlight.current = true;
    setBusy(true);
    try {
      await installPrompt.prompt();
      const choice = await installPrompt.userChoice;
      if (!isCurrent()) return;
      if (choice.outcome === "accepted") setInstalled(true);
      setInstallPrompt(null);
    } catch (error) {
      if (isCurrent()) setMessage(error instanceof Error ? error.message : "Kurulum başlatılamadı.");
    } finally {
      if (isCurrent()) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }

  async function enableNotifications() {
    const generation = epoch.current;
    const isCurrent = () => mounted.current && epoch.current === generation;
    if (!isCurrent() || inFlight.current) return;
    if (!configuration?.configured || !configuration.publicKey) {
      setMessage("Sunucu bildirim anahtarları henüz tanımlanmadı.");
      return;
    }
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setMessage("Bu telefon push bildirimini desteklemiyor.");
      return;
    }
    if (isIos && !standalone()) {
      setMessage("iPhone’da önce Safari paylaş menüsünden Ana Ekrana Ekle’yi seçin, sonra uygulamayı ana ekrandan açın.");
      return;
    }
    inFlight.current = true;
    setBusy(true);
    setMessage("");
    try {
      const permission = await Notification.requestPermission();
      if (!isCurrent()) return;
      if (permission !== "granted") throw new Error("Telefon ayarlarından ANL Teknik bildirimlerine izin verin.");
      const registration = await navigator.serviceWorker.ready;
      if (!isCurrent()) return;
      const existing = await registration.pushManager.getSubscription();
      if (!isCurrent()) return;
      const subscription = existing ?? await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey(configuration.publicKey),
      });
      if (!isCurrent()) return;
      await apiRequest("/api/production/technical-mobile/push-subscriptions", {
        method: "POST",
        body: JSON.stringify({ ...subscription.toJSON(), deviceLabel: navigator.userAgent }),
      });
      if (!isCurrent()) return;
      setSubscribed(true);
      setMessage("Bildirimler açık. Şimdi test göndererek ses ve teslimi kontrol edin.");
    } catch (error) {
      if (isCurrent()) setMessage(error instanceof Error ? error.message : "Bildirimler açılamadı.");
    } finally {
      if (isCurrent()) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }

  async function sendTest() {
    const generation = epoch.current;
    const isCurrent = () => mounted.current && epoch.current === generation;
    if (!isCurrent() || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setMessage("");
    try {
      await apiRequest("/api/production/technical-mobile/push-test", { method: "POST" });
      if (isCurrent()) setMessage("Test push servisine gönderildi. Telefon sessizde değilse sistem bildirim sesi duyulmalı.");
    } catch (error) {
      if (isCurrent()) setMessage(error instanceof Error ? error.message : "Test bildirimi gönderilemedi.");
    } finally {
      if (isCurrent()) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }

  async function logout() {
    const generation = epoch.current;
    const isCurrent = () => mounted.current && epoch.current === generation;
    if (!isCurrent() || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await apiRequest("/api/production/technical-mobile/push-subscriptions", {
            method: "DELETE",
            body: JSON.stringify({ endpoint: subscription.endpoint }),
          }).catch(() => undefined);
          await subscription.unsubscribe();
        }
      }
      await apiRequest("/api/production/technical-mobile/logout", { method: "POST" });
      if (isCurrent()) window.location.assign("/");
    } catch (error) {
      if (isCurrent()) setMessage(error instanceof Error ? error.message : "Oturum kapatılamadı.");
    } finally {
      if (isCurrent()) {
        inFlight.current = false;
        setBusy(false);
      }
    }
  }

  const setupComplete = installed && subscribed;
  return { identity, installPrompt, installed, subscribed, busy, message, isIos, setupComplete, install, enableNotifications, sendTest, logout };
}
